import React, { useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import { Box, Typography } from '@mui/material';
import { getWizardServiceConfig } from '../../../constants/wizardServiceRegistry';
import { CUSTOM_SERVICE_LABEL } from '../../../utils/serviceTypeDisplay';
import { getWizardUrgencyLabel } from '../../../constants/wizardUrgencyOptions';
import { buildWizardReviewPresentation } from '../../../utils/wizardRequestPresentation';
import WizardPhotoGallery from '../WizardPhotoGallery/WizardPhotoGallery';
import NkySpinner from '../NkySpinner/NkySpinner';
import {
  StyledRequestSummaryDialog,
  RequestSummaryDialogTitle,
  RequestSummaryDialogCloseButton,
  RequestSummaryDialogContent,
  RequestSummaryDialogActions,
  RequestSummaryCloseButton,
  RequestSummaryReviewList,
  RequestSummaryReviewItem,
  RequestSummaryReviewItemPhotos,
  RequestSummaryReviewLabel,
  RequestSummaryReviewValue,
  RequestSummaryReviewValuePhotos,
  RequestSummaryReviewValueList,
  RequestSummaryOutreachItem,
} from './WizardRequestSummaryDialog.styled';

function displayRefValue(value) {
  if (value == null) return '—';
  const s = String(value).trim();
  return s.length ? s : '—';
}

/**
 * Full wizard-style request summary (same content as the wizard review step).
 * Styled like ContactForServiceModal (gradient header, teal/blue CTA).
 */
const WizardRequestSummaryDialog = ({
  open,
  onClose,
  wizardState,
  serviceLabel,
  subserviceLabel,
  urgencyLabel,
  leads = [],
  businessNameById = {},
  helpRequested = false,
}) => {
  const isCustomInquiry = wizardState?.inquiryKind === 'custom';

  const leadEntries = useMemo(() => {
    if (!Array.isArray(leads) || leads.length === 0) return [];
    return leads.map((o, index) => ({
      key: `${o.businessId || 'biz'}-${o.sentAt || index}`,
      businessId: o.businessId,
      name: (o.businessId && businessNameById[o.businessId]) || null,
      sentAt: o.sentAt,
    }));
  }, [leads, businessNameById]);

  const leadNamesLoading = leadEntries.some((e) => e.businessId && !e.name);

  const customSummaryPresentation = useMemo(() => {
    if (!isCustomInquiry || !wizardState?.formData) return null;
    const rows = [
      {
        label: 'Service type',
        value: CUSTOM_SERVICE_LABEL,
      },
    ];
    const targetTitle = wizardState.targetServiceTitle;
    if (targetTitle) {
      rows.push({ label: 'Service', value: targetTitle });
    }
    const urgencyDisplay =
      wizardState.requestSummary?.urgency ||
      getWizardUrgencyLabel(wizardState.formData.urgency);
    if (urgencyDisplay && urgencyDisplay !== '—') {
      rows.push({ label: 'Urgency', value: urgencyDisplay });
    }
    const locationDisplay =
      wizardState.requestSummary?.location ||
      (wizardState.formData.city?.trim()
        ? [wizardState.formData.city.trim(), wizardState.formData.state?.trim() || 'KY']
            .filter(Boolean)
            .join(', ')
        : '');
    if (locationDisplay) {
      rows.push({ label: 'City', value: locationDisplay });
    }
    if (wizardState.formData.details?.trim()) {
      rows.push({ label: 'Details', value: wizardState.formData.details.trim() });
    }
    const photoItems = (wizardState.formData.photos || [])
      .filter(Boolean)
      .map((url, i) => ({ id: `photo-${i}`, url }));
    if (photoItems.length > 0) {
      rows.push({ label: 'Photos', value: `${photoItems.length} photo(s)` });
    }
    return {
      title: 'Your inquiry',
      subtitle: targetTitle || CUSTOM_SERVICE_LABEL,
      rows,
      photoItems,
    };
  }, [isCustomInquiry, wizardState]);

  const fullSummaryPresentation = useMemo(() => {
    if (customSummaryPresentation) return customSummaryPresentation;
    if (!wizardState?.formData || !wizardState?.serviceType) return null;
    const cfg = getWizardServiceConfig(wizardState.serviceType);
    const requestSummary =
      wizardState.requestSummary && typeof wizardState.requestSummary === 'object'
        ? wizardState.requestSummary
        : {};
    const fromFlow = cfg
      ? buildWizardReviewPresentation(cfg.flowConfig, wizardState.formData)
      : {
          title: 'Your request',
          subtitle: '',
          photoItems: [],
          rows: [
            { label: 'Problem', value: '—' },
            { label: 'Urgency', value: '—' },
            ...(wizardState.formData.details?.trim()
              ? [{ label: 'Details', value: wizardState.formData.details.trim() }]
              : []),
            { label: 'City', value: '—' },
          ],
        };
    const rows = fromFlow.rows.map((row) => {
      if (row.label === 'Problem' && (row.value === '—' || !row.value) && requestSummary.problemSummary) {
        return { ...row, value: String(requestSummary.problemSummary) };
      }
      if (row.label === 'Urgency' && (row.value === '—' || !row.value) && requestSummary.urgency) {
        return { ...row, value: String(requestSummary.urgency) };
      }
      if (row.label === 'City' && (row.value === '—' || !row.value) && requestSummary.location) {
        return { ...row, value: String(requestSummary.location) };
      }
      return row;
    });

    const existingLabels = new Set(rows.map((row) => row.label.trim().toLowerCase()));
    const additionalDetails = wizardState.formData.additionalDetails;
    const extras = [];
    if (additionalDetails && typeof additionalDetails === 'object') {
      Object.entries(additionalDetails).forEach(([key, value]) => {
        if (key === '_fieldIds' || value == null || typeof value === 'object') return;
        const label = String(key).replace(/[?]/g, '').trim();
        if (!label || existingLabels.has(label.toLowerCase())) return;
        extras.push({ label, value: String(value) });
        existingLabels.add(label.toLowerCase());
      });
    }
    if (extras.length) {
      const urgencyIdx = rows.findIndex((row) => row.label === 'Urgency');
      const insertAt = urgencyIdx === -1 ? Math.min(1, rows.length) : urgencyIdx;
      rows.splice(insertAt, 0, ...extras);
    }

    return { ...fromFlow, rows };
  }, [customSummaryPresentation, wizardState]);

  const titleText = fullSummaryPresentation?.title ?? 'Your request';
  const subtitleText = (fullSummaryPresentation?.subtitle || '').trim();

  const helpRequestedSection = helpRequested ? (
    <RequestSummaryReviewItem>
      <RequestSummaryReviewLabel>Help requested</RequestSummaryReviewLabel>
      <RequestSummaryReviewValue>NKY Connect will match you with a pro.</RequestSummaryReviewValue>
    </RequestSummaryReviewItem>
  ) : null;

  const sentBusinessesSection =
    leadEntries.length > 0 ? (
      <RequestSummaryReviewItem>
        <RequestSummaryReviewLabel>Sent to</RequestSummaryReviewLabel>
        {leadNamesLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <NkySpinner size="sm" />
            <Typography variant="body2" color="text.secondary">
              Loading businesses…
            </Typography>
          </Box>
        ) : (
          <RequestSummaryReviewValueList>
            {leadEntries.map((entry) => (
              <RequestSummaryOutreachItem key={entry.key}>
                {entry.name || 'Business'}
              </RequestSummaryOutreachItem>
            ))}
          </RequestSummaryReviewValueList>
        )}
      </RequestSummaryReviewItem>
    ) : null;

  return (
    <StyledRequestSummaryDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      aria-labelledby="wizard-request-summary-dialog-title"
    >
      <RequestSummaryDialogTitle id="wizard-request-summary-dialog-title">
        <span className="request-summary-dialog-icon-wrap" aria-hidden>
          <FactCheckRoundedIcon className="request-summary-dialog-icon" />
        </span>
        <Box className="request-summary-dialog-heading">
          <span className="request-summary-dialog-main-title">{titleText}</span>
          {subtitleText ? (
            <span className="request-summary-dialog-subtitle">{subtitleText}</span>
          ) : null}
        </Box>
        <RequestSummaryDialogCloseButton aria-label="Close summary" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </RequestSummaryDialogCloseButton>
      </RequestSummaryDialogTitle>

      <RequestSummaryDialogContent>
        {fullSummaryPresentation ? (
          <>
            <RequestSummaryReviewList>
              {fullSummaryPresentation.rows.map((row, i) => {
                const photoItems = fullSummaryPresentation.photoItems ?? [];
                const isPhotosRow = row.label === 'Photos' && photoItems.length > 0;
                const Item = isPhotosRow ? RequestSummaryReviewItemPhotos : RequestSummaryReviewItem;
                return (
                  <Item key={`${row.label}-${i}`}>
                    <RequestSummaryReviewLabel>{row.label}</RequestSummaryReviewLabel>
                    {isPhotosRow ? (
                      <RequestSummaryReviewValuePhotos>
                        <WizardPhotoGallery items={photoItems} inline />
                      </RequestSummaryReviewValuePhotos>
                    ) : (
                      <RequestSummaryReviewValue>{row.value}</RequestSummaryReviewValue>
                    )}
                  </Item>
                );
              })}
              {helpRequestedSection}
              {sentBusinessesSection}
            </RequestSummaryReviewList>
          </>
        ) : (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Summary details from your search:
            </Typography>
            <RequestSummaryReviewList>
              <RequestSummaryReviewItem>
                <RequestSummaryReviewLabel>Service</RequestSummaryReviewLabel>
                <RequestSummaryReviewValue>{displayRefValue(serviceLabel)}</RequestSummaryReviewValue>
              </RequestSummaryReviewItem>
              <RequestSummaryReviewItem>
                <RequestSummaryReviewLabel>Category</RequestSummaryReviewLabel>
                <RequestSummaryReviewValue>{displayRefValue(subserviceLabel)}</RequestSummaryReviewValue>
              </RequestSummaryReviewItem>
              <RequestSummaryReviewItem>
                <RequestSummaryReviewLabel>Urgency</RequestSummaryReviewLabel>
                <RequestSummaryReviewValue>{displayRefValue(urgencyLabel)}</RequestSummaryReviewValue>
              </RequestSummaryReviewItem>
              {helpRequestedSection}
              {sentBusinessesSection}
            </RequestSummaryReviewList>
          </Box>
        )}
      </RequestSummaryDialogContent>

      <RequestSummaryDialogActions>
        <RequestSummaryCloseButton variant="outlined" color="inherit" onClick={onClose}>
          Close
        </RequestSummaryCloseButton>
      </RequestSummaryDialogActions>
    </StyledRequestSummaryDialog>
  );
};

export default WizardRequestSummaryDialog;
